import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { sv } from 'date-fns/locale'
import {
  Search,
  ArrowDownLeft,
  ArrowUpRight,
  Bot,
  ChevronRight,
  MoreVertical,
  Link as LinkIcon,
  Unlink,
  MinusCircle,
  Filter,
  RefreshCw,
  Edit,
  User,
  Leaf,
  Shield,
  X
} from 'lucide-react'
import { getAllMessages, linkCommunicationToAssignment, dismissLinkSuggestion } from '../services/sms'
import { supabase } from '../lib/supabase'
import type { Communication, MessageListFilters } from '../types/communication'

interface MessageListViewProps {
  onSelectMessage: (message: Communication) => void
}

export default function MessageListView({ onSelectMessage }: MessageListViewProps) {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<MessageListFilters>({})
  const [page, setPage] = useState(1)
  const [showFilters, setShowFilters] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Load initial filters from sessionStorage
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('smsFilters')
      if (saved) {
        const savedFilters = JSON.parse(saved)
        setFilters(savedFilters)
        // Show filters panel if there are any specific filters set
        if (savedFilters.recipientId || savedFilters.recipientType || savedFilters.assignmentId) {
          setShowFilters(true)
        }
        // Set assignment filter input if assignmentNumber is set
        if (savedFilters.assignmentNumber) {
          setAssignmentFilterInput(String(savedFilters.assignmentNumber))
        }
        // Clear after loading to avoid persisting on page refresh
        sessionStorage.removeItem('smsFilters')
      }
    } catch (e) {
      console.error('Error loading SMS filters from sessionStorage:', e)
    }
  }, [])
  const [linkingMessageId, setLinkingMessageId] = useState<string | null>(null)
  const [linkDropdownOpenId, setLinkDropdownOpenId] = useState<string | null>(null)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const [assignmentSearch, setAssignmentSearch] = useState('')
  const [assignmentFilterInput, setAssignmentFilterInput] = useState('')
  const [limit, setLimit] = useState(20)

  // Fetch messages
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['all-messages', filters, page, limit],
    queryFn: () => getAllMessages({ ...filters, search: searchQuery }, page, limit),
    refetchInterval: 30000
  })

  const messages = data?.messages || []
  const total = data?.total || 0
  const totalPages = Math.ceil(total / limit)

  // Fetch customers for filter dropdown
  const { data: customersData } = useQuery({
    queryKey: ['customers-for-filter'],
    queryFn: async () => {
      const { data } = await supabase
        .from('customers')
        .select('id, full_name')
        .eq('active', true)
        .order('full_name')
      return data || []
    },
    enabled: showFilters
  })

  // Fetch subcontractors for filter dropdown
  const { data: subcontractorsData } = useQuery({
    queryKey: ['subcontractors-for-filter'],
    queryFn: async () => {
      const { data } = await supabase
        .from('subcontractors')
        .select('id, name')
        .eq('is_active', true)
        .order('name')
      return data || []
    },
    enabled: showFilters
  })

  // Fetch assignments for linking dropdown - trigger on either linkingMessageId or linkDropdownOpenId
  const activeMessageIdForLinking = linkingMessageId || linkDropdownOpenId
  const { data: assignmentsData } = useQuery({
    queryKey: ['assignments-for-linking', activeMessageIdForLinking],
    queryFn: async () => {
      if (!activeMessageIdForLinking) return []
      const message = messages.find(m => m.id === activeMessageIdForLinking)
      if (!message) return []

      if (message.recipient_type === 'customer') {
        // Fetch customer's assignments directly
        const { data } = await supabase
          .from('assignments')
          .select('id, assignment_number, title, status')
          .eq('customer_id', message.recipient_id)
          .not('status', 'in', '("slutford","avbrutten")')
          .order('assignment_number', { ascending: false })
          .limit(20)
        return data || []
      } else {
        // Fetch subcontractor's assignments via junction table
        const { data } = await supabase
          .from('assignment_subcontractors')
          .select(`
            assignment_id,
            assignments:assignment_id (
              id,
              title,
              assignment_number,
              status
            )
          `)
          .eq('subcontractor_id', message.recipient_id)
          .limit(20)

        // Extract assignments from the joined data and filter out completed/cancelled
        const assignments: { id: string; title: string; assignment_number: number; status: string }[] = []
        for (const row of data || []) {
          const a = row.assignments as unknown as { id: string; title: string; assignment_number: number; status: string } | null
          if (a && a.status !== 'slutford' && a.status !== 'avbrutten') {
            assignments.push({
              id: a.id,
              title: a.title,
              assignment_number: a.assignment_number,
              status: a.status
            })
          }
        }
        assignments.sort((a, b) => b.assignment_number - a.assignment_number)
        return assignments
      }
    },
    enabled: !!activeMessageIdForLinking
  })

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchQuery }))
    setPage(1)
  }

  const handleFilterChange = (key: keyof MessageListFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value === 'all' ? undefined : value }))
    setPage(1)
  }

  const clearFilters = () => {
    setFilters({})
    setSearchQuery('')
    setAssignmentFilterInput('')
    setPage(1)
  }

  const hasActiveFilters = Object.values(filters).some(v => v !== undefined) || searchQuery

  const handleLinkToAssignment = async (messageId: string, assignmentId: string) => {
    await linkCommunicationToAssignment(messageId, assignmentId)
    setLinkingMessageId(null)
    setLinkDropdownOpenId(null)
    setMenuOpenId(null)
    setAssignmentSearch('')
    refetch()
  }

  const handleDismissLink = async (messageId: string) => {
    await dismissLinkSuggestion(messageId)
    setLinkDropdownOpenId(null)
    setMenuOpenId(null)
    setAssignmentSearch('')
    refetch()
  }

  // Filter assignments based on search
  const filteredAssignments = (assignmentsData || []).filter((a: any) => {
    if (!assignmentSearch.trim()) return true
    const search = assignmentSearch.toLowerCase()
    return (
      String(a.assignment_number).includes(search) ||
      a.title?.toLowerCase().includes(search)
    )
  })

  return (
    <div className="flex flex-col h-full">
      {/* Search and Filter Bar */}
      <div className="flex-shrink-0 bg-white dark:bg-nature-800 border-b border-nature-200 dark:border-nature-700 p-4">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nature-400" />
            <input
              type="text"
              placeholder="Sök meddelanden..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-9 pr-4 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-nature-900 dark:text-nature-100 focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
              hasActiveFilters
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                : 'border-nature-300 dark:border-nature-600 hover:bg-nature-50 dark:hover:bg-nature-700'
            }`}
          >
            <Filter className="h-4 w-4" />
            Filter
            {hasActiveFilters && (
              <span className="bg-primary-500 text-white text-xs px-1.5 py-0.5 rounded-full">!</span>
            )}
          </button>

          {/* Limit selector */}
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value))
              setPage(1)
            }}
            className="px-2 py-2 text-sm border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700"
            title="Antal per sida"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={9999}>Alla</option>
          </select>

          {/* Refresh */}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2 rounded-lg border border-nature-300 dark:border-nature-600 hover:bg-nature-50 dark:hover:bg-nature-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Expandable Filters */}
        {showFilters && (
          <div className="mt-4 p-4 bg-nature-50 dark:bg-nature-900 rounded-lg space-y-3">
            {/* Show active person filter if set */}
            {filters.recipientId && filters.recipientName && (
              <div className="flex items-center gap-2 pb-3 border-b border-nature-200 dark:border-nature-700">
                <span className="text-sm text-nature-600 dark:text-nature-400">Visar SMS för:</span>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium ${
                  filters.recipientType === 'subcontractor'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                    : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                }`}>
                  {filters.recipientType === 'subcontractor' ? (
                    <Leaf className="h-3.5 w-3.5" />
                  ) : (
                    <User className="h-3.5 w-3.5" />
                  )}
                  {filters.recipientName}
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, recipientId: undefined, recipientName: undefined, recipientType: undefined }))}
                    className="ml-1 hover:bg-black/10 dark:hover:bg-white/10 rounded-full p-0.5"
                    title="Ta bort filter"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              </div>
            )}

            {/* Show active assignment filter if set */}
            {filters.assignmentId && filters.assignmentNumber && (
              <div className="flex items-center gap-2 pb-3 border-b border-nature-200 dark:border-nature-700">
                <span className="text-sm text-nature-600 dark:text-nature-400">Visar SMS för uppdrag:</span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                  <LinkIcon className="h-3.5 w-3.5" />
                  #{filters.assignmentNumber}
                  <button
                    onClick={() => {
                      setAssignmentFilterInput('')
                      setFilters(prev => ({ ...prev, assignmentId: undefined, assignmentNumber: undefined }))
                    }}
                    className="ml-1 hover:bg-black/10 dark:hover:bg-white/10 rounded-full p-0.5"
                    title="Ta bort filter"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Direction */}
              <div>
                <label className="block text-xs font-medium text-nature-600 dark:text-nature-400 mb-1">
                  Riktning
                </label>
                <select
                  value={filters.direction || 'all'}
                  onChange={(e) => handleFilterChange('direction', e.target.value)}
                  className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-sm"
                >
                  <option value="all">Alla</option>
                  <option value="inbound">Inkommande</option>
                  <option value="outbound">Utgående</option>
                </select>
              </div>

              {/* Customer filter */}
              <div>
                <label className="block text-xs font-medium text-nature-600 dark:text-nature-400 mb-1">
                  Kund
                </label>
                <select
                  value={filters.recipientType === 'customer' ? filters.recipientId || '' : ''}
                  onChange={(e) => {
                    const id = e.target.value
                    if (!id) {
                      // Clear customer filter
                      if (filters.recipientType === 'customer') {
                        setFilters(prev => ({ ...prev, recipientType: undefined, recipientId: undefined, recipientName: undefined }))
                      }
                    } else {
                      const name = customersData?.find(c => c.id === id)?.full_name || ''
                      setFilters(prev => ({ ...prev, recipientType: 'customer', recipientId: id, recipientName: name }))
                    }
                    setPage(1)
                  }}
                  className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-sm"
                >
                  <option value="">Alla kunder</option>
                  {customersData?.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.full_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subcontractor filter */}
              <div>
                <label className="block text-xs font-medium text-nature-600 dark:text-nature-400 mb-1">
                  Trädgårdsmästare
                </label>
                <select
                  value={filters.recipientType === 'subcontractor' ? filters.recipientId || '' : ''}
                  onChange={(e) => {
                    const id = e.target.value
                    if (!id) {
                      // Clear subcontractor filter
                      if (filters.recipientType === 'subcontractor') {
                        setFilters(prev => ({ ...prev, recipientType: undefined, recipientId: undefined, recipientName: undefined }))
                      }
                    } else {
                      const name = subcontractorsData?.find(s => s.id === id)?.name || ''
                      setFilters(prev => ({ ...prev, recipientType: 'subcontractor', recipientId: id, recipientName: name }))
                    }
                    setPage(1)
                  }}
                  className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-sm"
                >
                  <option value="">Alla trädgårdsmästare</option>
                  {subcontractorsData?.map(sub => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Assignment number filter */}
              <div>
                <label className="block text-xs font-medium text-nature-600 dark:text-nature-400 mb-1">
                  Uppdrag #
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="T.ex. 1234"
                    value={assignmentFilterInput}
                    onChange={(e) => setAssignmentFilterInput(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && assignmentFilterInput.trim()) {
                        const num = parseInt(assignmentFilterInput.trim())
                        if (!isNaN(num)) {
                          // Look up assignment by number
                          const { data } = await supabase
                            .from('assignments')
                            .select('id')
                            .eq('assignment_number', num)
                            .single()
                          if (data) {
                            setFilters(prev => ({ ...prev, assignmentId: data.id, assignmentNumber: num }))
                            setPage(1)
                          }
                        }
                      }
                    }}
                    onBlur={async () => {
                      if (assignmentFilterInput.trim()) {
                        const num = parseInt(assignmentFilterInput.trim())
                        if (!isNaN(num)) {
                          const { data } = await supabase
                            .from('assignments')
                            .select('id')
                            .eq('assignment_number', num)
                            .single()
                          if (data) {
                            setFilters(prev => ({ ...prev, assignmentId: data.id, assignmentNumber: num }))
                            setPage(1)
                          }
                        }
                      } else if (!assignmentFilterInput.trim() && filters.assignmentId) {
                        // Clear filter if input is empty
                        setFilters(prev => ({ ...prev, assignmentId: undefined, assignmentNumber: undefined }))
                        setPage(1)
                      }
                    }}
                    className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-sm"
                  />
                  {filters.assignmentId && (
                    <button
                      onClick={() => {
                        setAssignmentFilterInput('')
                        setFilters(prev => ({ ...prev, assignmentId: undefined, assignmentNumber: undefined }))
                        setPage(1)
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-nature-400 hover:text-nature-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Source Type - only applies to outbound */}
              <div>
                <label className="block text-xs font-medium text-nature-600 dark:text-nature-400 mb-1">
                  Utgående källa
                </label>
                <select
                  value={filters.isSystem === true ? 'system' : filters.isSystem === false ? 'manual' : 'all'}
                  onChange={(e) => {
                    const val = e.target.value
                    handleFilterChange('isSystem', val === 'system' ? true : val === 'manual' ? false : null)
                  }}
                  className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-sm"
                >
                  <option value="all">Alla</option>
                  <option value="manual">Admin (manuellt)</option>
                  <option value="system">System (automatiskt)</option>
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-medium text-nature-600 dark:text-nature-400 mb-1">
                  Status
                </label>
                <select
                  value={filters.status || 'all'}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-sm"
                >
                  <option value="all">Alla</option>
                  <option value="unread">Olästa</option>
                  <option value="failed">Misslyckade</option>
                </select>
              </div>

              {/* Linked Status */}
              <div>
                <label className="block text-xs font-medium text-nature-600 dark:text-nature-400 mb-1">
                  Länkstatus
                </label>
                <select
                  value={filters.isLinked === true ? 'linked' : filters.isLinked === false ? 'unlinked' : 'all'}
                  onChange={(e) => {
                    const val = e.target.value
                    handleFilterChange('isLinked', val === 'linked' ? true : val === 'unlinked' ? false : null)
                  }}
                  className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-sm"
                >
                  <option value="all">Alla</option>
                  <option value="linked">Kopplade</option>
                  <option value="unlinked">Ej kopplade</option>
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className="block text-xs font-medium text-nature-600 dark:text-nature-400 mb-1">
                  Från datum
                </label>
                <input
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value || undefined)}
                  className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-sm"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-xs font-medium text-nature-600 dark:text-nature-400 mb-1">
                  Till datum
                </label>
                <input
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value || undefined)}
                  className="w-full px-3 py-2 border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700 text-sm"
                />
              </div>

              {/* Clear Filters */}
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-3 py-2 text-sm text-nature-600 dark:text-nature-400 hover:text-nature-900 dark:hover:text-nature-100 border border-nature-300 dark:border-nature-600 rounded-lg hover:bg-nature-100 dark:hover:bg-nature-700"
                >
                  Rensa filter
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Messages List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-nature-400" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-nature-500">
            <Search className="h-12 w-12 mb-3 opacity-50" />
            <p>Inga meddelanden hittades</p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-2 text-primary-600 hover:underline"
              >
                Rensa filter
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-nature-200 dark:divide-nature-700">
            {messages.map((message) => {
              const isSystem = message.direction === 'outbound' && !message.sender_id
              const isUnread = message.direction === 'inbound' && !message.read_at
              const isFailed = message.status === 'failed'

              return (
                <div
                  key={message.id}
                  className="relative flex items-start gap-2 sm:gap-3 p-3 sm:p-4 hover:bg-nature-50 dark:hover:bg-nature-800/50 cursor-pointer transition-colors"
                  onClick={() => onSelectMessage(message)}
                >
                  {/* Direction Icon - hidden on mobile */}
                  <div className={`hidden sm:flex flex-shrink-0 w-10 h-10 rounded-full items-center justify-center ${
                    isSystem
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                      : message.direction === 'inbound'
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                  }`}>
                    {isSystem ? (
                      <Bot className="h-5 w-5" />
                    ) : message.direction === 'inbound' ? (
                      <ArrowDownLeft className="h-5 w-5" />
                    ) : (
                      <ArrowUpRight className="h-5 w-5" />
                    )}
                  </div>

                  {/* Mobile: Small direction indicator */}
                  <div className={`sm:hidden flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                    isSystem
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                      : message.direction === 'inbound'
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                        : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                  }`}>
                    {isSystem ? (
                      <Bot className="h-3 w-3" />
                    ) : message.direction === 'inbound' ? (
                      <ArrowDownLeft className="h-3 w-3" />
                    ) : (
                      <ArrowUpRight className="h-3 w-3" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {/* Header row: name + unread indicator */}
                    <div className="flex items-center gap-1.5">
                      {isUnread && (
                        <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
                      )}
                      <span className="font-medium text-nature-900 dark:text-nature-100">
                        {message.recipient_name}
                      </span>
                      {isFailed && (
                        <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                          !
                        </span>
                      )}
                    </div>

                    {/* Badge row */}
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {isSystem ? (
                        <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300">
                          <Shield className="h-3 w-3" />
                          Admin
                        </span>
                      ) : message.recipient_type === 'customer' ? (
                        <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                          <User className="h-3 w-3" />
                          Kund
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                          <Leaf className="h-3 w-3" />
                          Trädgårdsmästare
                        </span>
                      )}
                    </div>

                    {/* Message preview */}
                    <p className="text-sm text-nature-600 dark:text-nature-400 mt-0.5 whitespace-pre-wrap break-words">
                      {message.message}
                    </p>

                    {/* Mobile: Assignment link with edit option */}
                    <div className="sm:hidden mt-1.5 text-xs">
                      {message.assignments ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/admin/assignments/${message.assignment_id}`)
                            }}
                            className="flex items-center gap-1 text-primary-600 dark:text-primary-400"
                          >
                            <LinkIcon className="h-3 w-3" />
                            #{message.assignments.assignment_number}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setLinkDropdownOpenId(linkDropdownOpenId === message.id ? null : message.id)
                            }}
                            className="p-1 text-nature-400"
                          >
                            <Edit className="h-3 w-3" />
                          </button>
                        </div>
                      ) : message.link_dismissed ? (
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1 text-nature-400">
                            <MinusCircle className="h-3 w-3" />
                            Ingen koppling
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setLinkDropdownOpenId(linkDropdownOpenId === message.id ? null : message.id)
                            }}
                            className="p-1 text-nature-400"
                          >
                            <Edit className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setLinkDropdownOpenId(linkDropdownOpenId === message.id ? null : message.id)
                          }}
                          className="flex items-center gap-1 text-amber-600 dark:text-amber-400"
                        >
                          <Unlink className="h-3 w-3" />
                          Ej kopplat
                        </button>
                      )}

                      {/* Mobile dropdown - fixed position at bottom */}
                      {linkDropdownOpenId === message.id && (
                        <div
                          className="fixed inset-x-0 bottom-0 z-30 bg-white dark:bg-nature-800 rounded-t-xl shadow-lg border-t border-nature-200 dark:border-nature-700 max-h-[60vh] overflow-hidden"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="px-4 py-3 border-b border-nature-200 dark:border-nature-700 flex items-center justify-between">
                            <span className="font-medium text-nature-900 dark:text-nature-100">
                              {message.assignments ? 'Ändra koppling' : 'Koppla till uppdrag'}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setLinkDropdownOpenId(null)
                                setAssignmentSearch('')
                              }}
                              className="p-1 text-nature-500"
                            >
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                          <div className="px-4 py-2 border-b border-nature-200 dark:border-nature-700">
                            <input
                              type="text"
                              placeholder="Sök uppdrag..."
                              value={assignmentSearch}
                              onChange={(e) => setAssignmentSearch(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-3 py-2 text-sm border border-nature-300 dark:border-nature-600 rounded-lg bg-white dark:bg-nature-700"
                              autoFocus
                            />
                          </div>
                          <div className="overflow-y-auto max-h-[40vh]">
                            {filteredAssignments.length > 0 ? (
                              filteredAssignments.map((assignment: any) => (
                                <button
                                  key={assignment.id}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleLinkToAssignment(message.id, assignment.id)
                                  }}
                                  className={`w-full text-left px-4 py-3 text-sm border-b border-nature-100 dark:border-nature-700 ${
                                    assignment.id === message.assignment_id
                                      ? 'bg-primary-50 dark:bg-primary-900/20'
                                      : 'hover:bg-nature-50 dark:hover:bg-nature-700'
                                  }`}
                                >
                                  #{assignment.assignment_number} - {assignment.title}
                                  {assignment.id === message.assignment_id && ' ✓'}
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-3 text-sm text-nature-500">
                                {assignmentSearch ? 'Inga matchande uppdrag' : 'Inga uppdrag'}
                              </div>
                            )}
                          </div>
                          {message.assignments && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDismissLink(message.id)
                              }}
                              className="w-full text-left px-4 py-3 text-sm text-red-600 dark:text-red-400 border-t border-nature-200 dark:border-nature-700 flex items-center gap-2"
                            >
                              <MinusCircle className="h-4 w-4" />
                              Ta bort koppling
                            </button>
                          )}
                          {!message.assignments && !message.link_dismissed && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDismissLink(message.id)
                              }}
                              className="w-full text-left px-4 py-3 text-sm text-nature-500 border-t border-nature-200 dark:border-nature-700 flex items-center gap-2"
                            >
                              <MinusCircle className="h-4 w-4" />
                              Ingen koppling
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Assignment link - hidden on mobile, visible on sm+ */}
                    <div className="text-xs mt-1 relative hidden sm:block">
                      {message.assignments ? (
                        // Linked to assignment - clickable to navigate, with change button
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/admin/assignments/${message.assignment_id}`)
                            }}
                            className="flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline truncate max-w-[250px]"
                          >
                            <LinkIcon className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">#{message.assignments.assignment_number} - {message.assignments.title}</span>
                          </button>
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setLinkDropdownOpenId(linkDropdownOpenId === message.id ? null : message.id)
                              }}
                              className="text-nature-400 hover:text-nature-600 dark:hover:text-nature-300"
                              title="Ändra koppling"
                            >
                              <Edit className="h-3 w-3" />
                            </button>
                            {/* Re-link dropdown for already linked messages */}
                            {linkDropdownOpenId === message.id && (
                              <div className="absolute left-0 top-5 z-20 w-64 bg-white dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 py-1">
                                <div className="px-3 py-2 text-xs font-medium text-nature-500 border-b border-nature-200 dark:border-nature-700">
                                  Ändra koppling
                                </div>
                                <div className="px-2 py-2 border-b border-nature-200 dark:border-nature-700">
                                  <input
                                    type="text"
                                    placeholder="Sök uppdrag (nr eller titel)..."
                                    value={assignmentSearch}
                                    onChange={(e) => setAssignmentSearch(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-full px-2 py-1 text-sm border border-nature-300 dark:border-nature-600 rounded bg-white dark:bg-nature-700"
                                    autoFocus
                                  />
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                  {filteredAssignments.length > 0 ? (
                                    filteredAssignments.map((assignment: any) => (
                                      <button
                                        key={assignment.id}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleLinkToAssignment(message.id, assignment.id)
                                        }}
                                        className={`w-full text-left px-3 py-2 text-sm hover:bg-nature-100 dark:hover:bg-nature-700 ${
                                          assignment.id === message.assignment_id ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                                        }`}
                                      >
                                        #{assignment.assignment_number} - {assignment.title}
                                        {assignment.id === message.assignment_id && ' ✓'}
                                      </button>
                                    ))
                                  ) : (
                                    <div className="px-3 py-2 text-sm text-nature-500">
                                      {assignmentSearch ? 'Inga matchande uppdrag' : 'Inga uppdrag'}
                                    </div>
                                  )}
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDismissLink(message.id)
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm text-nature-500 hover:bg-nature-100 dark:hover:bg-nature-700 border-t border-nature-200 dark:border-nature-700 flex items-center gap-2"
                                >
                                  <MinusCircle className="h-3 w-3" />
                                  Ta bort koppling
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : message.link_dismissed ? (
                        // Dismissed - show "Ingen koppling" but still allow re-linking
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1 text-nature-400 dark:text-nature-500">
                            <MinusCircle className="h-3 w-3" />
                            Ingen koppling
                          </span>
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setLinkDropdownOpenId(linkDropdownOpenId === message.id ? null : message.id)
                              }}
                              className="text-nature-400 hover:text-nature-600 dark:hover:text-nature-300"
                              title="Lägg till koppling"
                            >
                              <Edit className="h-3 w-3" />
                            </button>
                            {/* Re-link dropdown for dismissed messages */}
                            {linkDropdownOpenId === message.id && (
                              <div className="absolute left-0 top-5 z-20 w-64 bg-white dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 py-1">
                                <div className="px-3 py-2 text-xs font-medium text-nature-500 border-b border-nature-200 dark:border-nature-700">
                                  Koppla till uppdrag
                                </div>
                                <div className="px-2 py-2 border-b border-nature-200 dark:border-nature-700">
                                  <input
                                    type="text"
                                    placeholder="Sök uppdrag (nr eller titel)..."
                                    value={assignmentSearch}
                                    onChange={(e) => setAssignmentSearch(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-full px-2 py-1 text-sm border border-nature-300 dark:border-nature-600 rounded bg-white dark:bg-nature-700"
                                    autoFocus
                                  />
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                  {filteredAssignments.length > 0 ? (
                                    filteredAssignments.map((assignment: any) => (
                                      <button
                                        key={assignment.id}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleLinkToAssignment(message.id, assignment.id)
                                        }}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-nature-100 dark:hover:bg-nature-700"
                                      >
                                        #{assignment.assignment_number} - {assignment.title}
                                      </button>
                                    ))
                                  ) : (
                                    <div className="px-3 py-2 text-sm text-nature-500">
                                      {assignmentSearch ? 'Inga matchande uppdrag' : 'Inga uppdrag'}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        // Not linked - clickable to show dropdown
                        <div className="relative inline-block">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setLinkDropdownOpenId(linkDropdownOpenId === message.id ? null : message.id)
                            }}
                            className="flex items-center gap-1 text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 hover:underline"
                          >
                            <Unlink className="h-3 w-3" />
                            Ej kopplat till uppdrag
                          </button>

                          {/* Link dropdown */}
                          {linkDropdownOpenId === message.id && (
                            <div className="absolute left-0 top-6 z-20 w-64 bg-white dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 py-1">
                              <div className="px-3 py-2 text-xs font-medium text-nature-500 border-b border-nature-200 dark:border-nature-700">
                                Koppla till uppdrag
                              </div>
                              <div className="px-2 py-2 border-b border-nature-200 dark:border-nature-700">
                                <input
                                  type="text"
                                  placeholder="Sök uppdrag (nr eller titel)..."
                                  value={assignmentSearch}
                                  onChange={(e) => setAssignmentSearch(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-full px-2 py-1 text-sm border border-nature-300 dark:border-nature-600 rounded bg-white dark:bg-nature-700"
                                  autoFocus
                                />
                              </div>
                              <div className="max-h-48 overflow-y-auto">
                                {filteredAssignments.length > 0 ? (
                                  filteredAssignments.map((assignment: any) => (
                                    <button
                                      key={assignment.id}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleLinkToAssignment(message.id, assignment.id)
                                      }}
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-nature-100 dark:hover:bg-nature-700"
                                    >
                                      #{assignment.assignment_number} - {assignment.title}
                                    </button>
                                  ))
                                ) : (
                                  <div className="px-3 py-2 text-sm text-nature-500">
                                    {assignmentSearch ? 'Inga matchande uppdrag' : 'Inga uppdrag'}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDismissLink(message.id)
                                }}
                                className="w-full text-left px-3 py-2 text-sm text-nature-500 hover:bg-nature-100 dark:hover:bg-nature-700 border-t border-nature-200 dark:border-nature-700 flex items-center gap-2"
                              >
                                <MinusCircle className="h-3 w-3" />
                                Ingen koppling
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Timestamp - date and time on two lines */}
                  <div className="flex-shrink-0 text-right">
                    <p className="text-xs text-nature-400">
                      {format(new Date(message.created_at), 'yyyy-MM-dd', { locale: sv })}
                    </p>
                    <p className="text-xs text-nature-400">
                      {format(new Date(message.created_at), 'HH:mm', { locale: sv })}
                    </p>
                  </div>

                  {/* Three-dot menu - hidden on mobile */}
                  <div className="relative flex-shrink-0 hidden sm:block">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setMenuOpenId(menuOpenId === message.id ? null : message.id)
                        if (!message.assignment_id) {
                          setLinkingMessageId(message.id)
                        }
                      }}
                      className="p-1 rounded hover:bg-nature-200 dark:hover:bg-nature-600"
                    >
                      <MoreVertical className="h-4 w-4 text-nature-500" />
                    </button>

                    {/* Dropdown menu */}
                    {menuOpenId === message.id && (
                      <div className="absolute right-0 top-8 z-10 w-56 bg-white dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 py-1">
                        {!message.assignment_id && !message.link_dismissed && (
                          <>
                            <div className="px-3 py-2 text-xs font-medium text-nature-500 border-b border-nature-200 dark:border-nature-700">
                              Koppla till uppdrag
                            </div>
                            {assignmentsData && assignmentsData.length > 0 ? (
                              assignmentsData.map((assignment: any) => (
                                <button
                                  key={assignment.id}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleLinkToAssignment(message.id, assignment.id)
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-nature-100 dark:hover:bg-nature-700"
                                >
                                  #{assignment.assignment_number} - {assignment.title}
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-nature-500">
                                Inga aktiva uppdrag
                              </div>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDismissLink(message.id)
                              }}
                              className="w-full text-left px-3 py-2 text-sm text-nature-500 hover:bg-nature-100 dark:hover:bg-nature-700 border-t border-nature-200 dark:border-nature-700"
                            >
                              Markera som ej relevant
                            </button>
                          </>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            onSelectMessage(message)
                            setMenuOpenId(null)
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-nature-100 dark:hover:bg-nature-700 flex items-center gap-2"
                        >
                          <ChevronRight className="h-4 w-4" />
                          Öppna konversation
                        </button>
                      </div>
                    )}
                  </div>

                  <ChevronRight className="hidden sm:block flex-shrink-0 h-5 w-5 text-nature-400" />
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex-shrink-0 flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-t border-nature-200 dark:border-nature-700 bg-white dark:bg-nature-800">
          <span className="text-xs sm:text-sm text-nature-600 dark:text-nature-400">
            Sida {page} av {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-2 sm:px-3 py-1 text-xs sm:text-sm border border-nature-300 dark:border-nature-600 rounded hover:bg-nature-100 dark:hover:bg-nature-700 disabled:opacity-50"
            >
              Föregående
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-2 sm:px-3 py-1 text-xs sm:text-sm border border-nature-300 dark:border-nature-600 rounded hover:bg-nature-100 dark:hover:bg-nature-700 disabled:opacity-50"
            >
              Nästa
            </button>
          </div>
        </div>
      )}

      {/* Click outside to close menus */}
      {(menuOpenId || linkDropdownOpenId) && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => {
            setMenuOpenId(null)
            setLinkingMessageId(null)
            setLinkDropdownOpenId(null)
            setAssignmentSearch('')
          }}
        />
      )}
    </div>
  )
}
